import { useEffect, useRef } from 'react';
import { collection, query, where, onSnapshot, doc, updateDoc, runTransaction } from 'firebase/firestore';
import { db } from '../firebase';
import { RESTAURANT_ID } from '../constants';
import { playBeep } from '../utils/sound';
import { printNewOrderReceipt, printCancelReceipt, printExtraItemsReceipt } from '../utils/printer';

export function useOrderListener({ user, autoPrintRef, autoAccept, soundEnabledRef, onNewOrders, onCancelOrders }) {
  const autoAcceptRef = useRef(autoAccept);
  const autoAcceptTimers = useRef({});
  const knownOrders = useRef(new Set());
  const knownStatuses = useRef({});
  const knownItemAddedAt = useRef({});
  const printedOrders = useRef(new Set());
  const printedCancelOrders = useRef(new Set());
  const printedExtraOrders = useRef(new Set());
  const isFirstLoad = useRef(true);

  useEffect(() => {
    autoAcceptRef.current = autoAccept;
    if (!autoAccept) {
      Object.values(autoAcceptTimers.current).forEach(clearTimeout);
      autoAcceptTimers.current = {};
    }
  }, [autoAccept]);

  useEffect(() => {
    if (!user) return;

    const q = query(collection(db, 'orders'), where('restaurantId', '==', RESTAURANT_ID));
    const unsubscribe = onSnapshot(q, (snap) => {
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));

      if (isFirstLoad.current) {
        data.forEach(o => {
          knownOrders.current.add(o.id);
          knownStatuses.current[o.id] = o.status;
          knownItemAddedAt.current[o.id] = o.lastItemAddedAt?.toMillis?.() || null;
          printedOrders.current.add(o.id);
          if (o.status === 'cancelled') printedCancelOrders.current.add(o.id);
          const addedAt = o.lastItemAddedAt?.toMillis?.() || null;
          if (addedAt) printedExtraOrders.current.add(`${o.id}_${addedAt}`);

          if (o.status === 'pending' && autoAcceptRef.current) {
            const createdAt = o.createdAt?.toMillis?.() || Date.now();
            const elapsed = (Date.now() - createdAt) / 1000;
            const remaining = Math.max(0, 60 - elapsed);
            if (remaining > 0 && !autoAcceptTimers.current[o.id]) {
              autoAcceptTimers.current[o.id] = setTimeout(async () => {
                try {
                  const orderRef = doc(db, 'orders', o.id);
                  await runTransaction(db, async (tx) => {
                    const snap = await tx.get(orderRef);
                    // Sadece serverVerified=true ise otomatik kabul et.
                    if (snap.exists() && snap.data().status === 'pending' && snap.data().serverVerified === true) {
                      tx.update(orderRef, { status: 'preparing' });
                    }
                  });
                } catch (e) { console.error('Otomatik kabul başarısız:', e.code || 'unknown'); }
                delete autoAcceptTimers.current[o.id];
              }, remaining * 1000);
            }
          }
        });
        isFirstLoad.current = false;
        return;
      }

      let newCount = 0;
      let cancelCount = 0;

      data.forEach(order => {
        const isNew = !knownOrders.current.has(order.id);

        if (isNew) {
          knownOrders.current.add(order.id);
          knownStatuses.current[order.id] = order.status;
          knownItemAddedAt.current[order.id] = order.lastItemAddedAt?.toMillis?.() || null;
          if (order.status === 'pending') newCount++;
        }

        if (autoAcceptRef.current) {
          if (order.status === 'pending' && !autoAcceptTimers.current[order.id]) {
            const createdAt = order.createdAt?.toMillis?.() || Date.now();
            const elapsed = (Date.now() - createdAt) / 1000;
            const remaining = Math.max(0, 60 - elapsed);
            autoAcceptTimers.current[order.id] = setTimeout(async () => {
              try {
                const orderRef = doc(db, 'orders', order.id);
                await runTransaction(db, async (tx) => {
                  const snap = await tx.get(orderRef);
                  if (snap.exists() && snap.data().status === 'pending') {
                    tx.update(orderRef, { status: 'preparing' });
                  }
                });
              } catch (e) { console.error('Otomatik kabul başarısız:', e.code || 'unknown'); }
              delete autoAcceptTimers.current[order.id];
            }, remaining * 1000);
          }
        }

        if (order.status !== 'pending' && autoAcceptTimers.current[order.id]) {
          clearTimeout(autoAcceptTimers.current[order.id]);
          delete autoAcceptTimers.current[order.id];
        }

        if (order.status === 'preparing' && !printedOrders.current.has(order.id)) {
          printedOrders.current.add(order.id);
          if (autoPrintRef.current) setTimeout(() => printNewOrderReceipt(order), 500);
        }

        if (order.status === 'cancelled' && !printedCancelOrders.current.has(order.id)) {
          if (knownStatuses.current[order.id] !== 'cancelled') cancelCount++;
          printedCancelOrders.current.add(order.id);
          if (autoPrintRef.current) setTimeout(() => printCancelReceipt(order), 500);
        }

        const prevAddedAt = knownItemAddedAt.current[order.id];
        const currAddedAt = order.lastItemAddedAt?.toMillis?.() || null;
        const extraKey = `${order.id}_${currAddedAt}`;

        if (currAddedAt && prevAddedAt !== undefined && currAddedAt !== prevAddedAt && !printedExtraOrders.current.has(extraKey)) {
          printedExtraOrders.current.add(extraKey);
          knownItemAddedAt.current[order.id] = currAddedAt;
          if (soundEnabledRef.current) playBeep('new');
          onNewOrders(1);
          if (autoPrintRef.current && order.status !== 'pending') {
            const lastItem = order.items?.[order.items.length - 1];
            if (lastItem) setTimeout(() => printExtraItemsReceipt(order, [lastItem]), 500);
          }
        }

        knownStatuses.current[order.id] = order.status;
        if (!isNew) knownItemAddedAt.current[order.id] = currAddedAt;
      });

      if (newCount > 0) {
        onNewOrders(newCount);
        if (soundEnabledRef.current) playBeep('new');
      }
      if (cancelCount > 0) {
        onCancelOrders(cancelCount);
        if (soundEnabledRef.current) playBeep('cancel');
      }
    });

    return () => {
      unsubscribe();
      Object.values(autoAcceptTimers.current).forEach(clearTimeout);
      autoAcceptTimers.current = {};
    };
  }, [user]);

  const reset = () => {
    isFirstLoad.current = true;
    knownOrders.current.clear();
    knownStatuses.current = {};
    knownItemAddedAt.current = {};
    printedOrders.current.clear();
    printedCancelOrders.current.clear();
    printedExtraOrders.current.clear();
    Object.values(autoAcceptTimers.current).forEach(clearTimeout);
    autoAcceptTimers.current = {};
  };

  return { reset };
}
